<template>
  <label v-if="titulo"> {{ titulo }} </label>
  <DxDropDownBox ref="dropDownRef" :value="gridBoxValue" :defer-rendering="false" :placeholder="placeholder" :drop-down-options="{ width: dropdownWidth }"
    :disabled="props.disabled" field-template="field" @value-changed="(e:any) => gridBoxValue = e.value">
    <template #field>
      <div>
        <DxTagBox ref="tagBoxRef" :data-source="gridSource" v-model="gridBoxValue" :value-expr="props.valueExpr"
          :display-expr="getDisplayExpr" :open-on-field-click="false" :placeholder="placeholder" />
      </div>
    </template>
    <template #content="{ data }">
      <DxDataGrid :data-source="gridSource" ref="grid" :height="gridHeight ?? 300" v-model:selected-row-keys="gridBoxValue">
        <template v-for="col in props.columns" :key="col.dataField">
          <DxColumn :data-field="col.dataField" :caption="col.caption" :data-type="col.dataType || 'string'"
            :visible="col.visible !== false" :cell-template="col.template ?? ''" :format="col.format ?? ''"
            :min-width="col.minWidth ?? 0"/>
        </template>
        <DxSelection :mode="selectionMode" />
        <DxPaging :enabled="true" :page-size="pageSize ?? 10" />
        <DxPager :visible="true" :allowed-page-sizes="pageSizes" display-mode="full" :show-page-size-selector="true"
          :show-info="true" :show-navigation-buttons="true" />
        <DxScrolling mode="standard" />
        <DxFilterRow :visible="true" />
        <template #estadoTramiteTemplate="{ data }">

          <div :class="[
            `d-inline-block rounded py-1 px-2 `,
            `estado-operacion ${data.value.replace(' ', '')}`,
          ]">
              {{ $t(`operaciones.estado_calculado.${data.value}`) }}
          </div>
        </template>
        <template #serviciosTramiteTemplate="{ data }">
            <div v-for="servicio in data.value" :key="servicio" :class="[`d-inline-block rounded py-1 px-2 me-2 servicio ${servicio.replace(' ', '')}`]">
              <i :class="[
                'me-1',
                servicio === 'AGUA' ? 'fa-regular fa-droplet' :
                servicio === 'LUZ' ? 'fa-regular fa-lightbulb' :
                    'fa-regular fa-fire'
              ]"></i>
              {{ $t(`operaciones.tipo_suministro.${servicio}`) }}
            </div>
        </template>
        <template #booleanTemplate="{ data }">
          <BooleanTemplate :value="data.value" />
        </template>
      </DxDataGrid>
    </template>
  </DxDropDownBox>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import DxDropDownBox from 'devextreme-vue/drop-down-box';
import { DxDataGrid, DxSelection, DxPaging, DxFilterRow, DxPager, DxScrolling, DxColumn } from 'devextreme-vue/data-grid';
import { DxTagBox } from "devextreme-vue";
import { useApi } from "@/.shared/.api/integration";
import type { DXLoadOptions } from "@api/api";
import CustomStore from "devextreme/data/custom_store";
import type { PagerPageSize } from 'devextreme/ui/data_grid';
import BooleanTemplate from "./BooleanTemplate.vue";

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const dropDownRef = ref<DxDropDownBox>();
const tagBoxRef = ref<DxTagBox>();
const pageSizes: (number | PagerPageSize)[] = [10, 25, 50, 100];

const props = defineProps<{
  modelValue: string[] | undefined,
  titulo?: string,
  selectionMode: string,
  placeholder: string,
  valueExpr: string,
  displayExpr: string | ((data: any) => string),
  funcionApi?: string,
  keyApi: string,
  pageSize?: number,
  loadData?: (options: DXLoadOptions) => Promise<any>,
  columns: Array<{ dataField: string, caption?: string, dataType?: string, visible?: boolean, template?: string, format?: string, minWidth?: number }>,
  disabled?: boolean,
  dropdownWidth?: number | string,
  gridHeight?: number | string
}>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>();

const gridBoxValue = ref<string[]>(props.modelValue ?? []);

watch(() => props.modelValue, (val) => {
  gridBoxValue.value = val ?? [];
});

watch(gridBoxValue, (val) => {
  emit('update:modelValue', val ?? []);

  //cerramos el dropdown cuando selecciona algo y está en seleccion unica
  if (props.selectionMode === "single" && val && val.length > 0 && dropDownRef.value?.instance) {
    dropDownRef.value.instance.close();
  }
});

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: props.keyApi,
    load: props.loadData
      ? props.loadData
      : await api.toStandardApiFunction(props.funcionApi as any, { showLoading: false })
  });

  //forzamos la primera carga de datos
  setTimeout(() => {
    if (grid.value?.instance && gridBoxValue.value.length > 0) {
      grid.value.instance.selectRows(gridBoxValue.value, false);
    }
  }, 200);
});

const getDisplayExpr = computed(() => {
  if (typeof props.displayExpr === 'function') {
    return (data: any | undefined | null) => {
      return (props.displayExpr as (data: any) => string)(data);
    };
  }
  return props.displayExpr;
});

const clearSelection = () => {
  tagBoxRef.value?.instance?.clear();
};

const refreshGrid = () => {
  grid.value?.instance?.refresh();
};

const getSelectedRowsData = async (): Promise<any[]> => {
  if (!grid.value?.instance) {
    return [];
  }
  const data = await grid.value.instance.getSelectedRowsData();
  return data ?? [];
};

defineExpose({
  clearSelection, 
  refreshGrid,
  getSelectedRowsData
});

</script>
